"use client";

import { useMemo, useTransition } from "react";
import { useRouter } from "next/navigation";
import { CalendarDays } from "lucide-react";
import type { Market } from "@/lib/api";
import { formatDateTr } from "@/lib/date-format";
import { primaryMarketForCity } from "@/lib/market-route";
import CityCombobox from "@/components/sections/CityCombobox";

type RouteMarket = Pick<Market, "slug" | "name" | "cityName">;

export default function MarketRouteSelector({ markets, activeCity, latestDate, basePath = "/hal", sourceLabel = "Hal" }: { markets: RouteMarket[]; activeCity: string; latestDate: string | null; basePath?: "/hal" | "/borsa"; sourceLabel?: "Hal" | "Borsa" }) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const cities = useMemo(
    () => [...new Set(markets.map((market) => market.cityName))].sort((left, right) => left.localeCompare(right, "tr")),
    [markets],
  );

  function selectCity(city: string) {
    const market = primaryMarketForCity(markets, city);
    if (!market) return;
    startTransition(() => router.push(`${basePath}/${market.slug}`));
  }

  return (
    <div className="mt-6 grid max-w-[520px] gap-2 sm:grid-cols-[minmax(0,1fr)_190px]" aria-label={`Şehir ve ${sourceLabel.toLocaleLowerCase("tr-TR")} seçimi`}>
      <CityCombobox cities={cities} value={activeCity} onChange={selectCity} />
      <div className="flex min-h-12 items-center gap-2 border border-[#d9dfdb] bg-[#f7f8f6] px-3 text-sm font-semibold text-[#4f5a54]">
        <CalendarDays className="size-4 shrink-0" aria-hidden />
        <time dateTime={latestDate ?? undefined}>{isPending ? `${sourceLabel} açılıyor…` : formatDateTr(latestDate) ?? "Kayıt bekleniyor"}</time>
      </div>
    </div>
  );
}
